// Phase 17 · AI Insights page — anomalies + churn + forecast + stock-out
import { useEffect, useState } from "react";
import { Loader2, AlertCircle, TrendingDown, TrendingUp, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KIND_LABEL = {
  se_order_value_spike: "Order value spike",
  se_order_value_drop:  "Order value drop",
  distributor_claim_spike: "Claim spike",
  edge_of_radius_streak: "Edge-of-radius streak",
  duplicate_submission: "Duplicate submission",
};

export default function InsightsPage() {
  const [tab, setTab] = useState("anomalies");
  const [anomalies, setAnomalies] = useState(null);
  const [churn, setChurn] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [stockOut, setStockOut] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c, f, s] = await Promise.all([
        api.get("/ai/anomalies?page_size=20"),
        api.get("/ai/predict/churn-risk?limit=20"),
        api.get("/ai/predict/sales-forecast?scope=zone&months=3"),
        api.get("/ai/predict/stock-out-risk?top_n=20"),
      ]);
      setAnomalies(a.data); setChurn(c.data); setForecast(f.data); setStockOut(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load insights");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runDetect = async () => {
    try { const r = await api.post("/ai/anomalies/detect/run"); toast.success(`Detected ${r.data.created} new`); load(); }
    catch (e) { toast.error("Detection failed"); }
  };
  const dismiss = async (id) => {
    try { await api.post(`/ai/anomalies/${id}/dismiss`); load(); toast.success("Dismissed"); }
    catch (e) { toast.error("Dismiss failed"); }
  };

  if (loading) return <div className="flex justify-center py-20 text-neutral-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="insights-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#E5197F]" /> AI Insights
          </h1>
          <p className="text-xs text-neutral-500 mt-1">Estimates improve with more history.</p>
        </div>
        <button onClick={runDetect} data-testid="run-detect-btn"
                 className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-[#E5197F] text-white text-xs">
          Re-run detection
        </button>
      </div>

      <div className="flex gap-1 border-b border-neutral-200">
        {["anomalies","churn","forecast","stockout"].map(t => (
          <button key={t} onClick={() => setTab(t)}
                   data-testid={`tab-${t}`}
                   className={`px-4 py-2 text-sm font-medium ${tab === t ? "text-[#E5197F] border-b-2 border-[#E5197F]" : "text-neutral-500"}`}>
            {t === "anomalies" ? `Anomalies (${anomalies?.total || 0})` :
             t === "churn" ? `Churn (${churn?.total || 0})` :
             t === "forecast" ? "Forecast" : `Stock-out (${stockOut?.total || 0})`}
          </button>
        ))}
      </div>

      {tab === "anomalies" && (
        <div className="space-y-2">
          {(anomalies?.items || []).map(a => (
            <div key={a.id} className={`bg-white rounded-xl border p-3 flex items-start justify-between gap-3 ${a.severity === "high" ? "border-rose-200 bg-rose-50" : ""}`}
                  data-testid={`anom-${a.id}`}>
              <div className="flex items-start gap-2">
                <AlertCircle className={`w-4 h-4 mt-0.5 ${a.severity === "high" ? "text-rose-600" : "text-amber-600"}`} />
                <div>
                  <div className="text-sm font-semibold">{KIND_LABEL[a.kind] || a.kind} · <span className="uppercase text-xs">{a.severity}</span></div>
                  <div className="text-xs text-neutral-700 mt-1">{a.explanation}</div>
                  <div className="text-[10px] text-neutral-400 mt-1">{a.detected_at?.slice(0,16).replace("T"," ")}</div>
                </div>
              </div>
              <button onClick={() => dismiss(a.id)} data-testid={`anom-dismiss-${a.id}`}
                       className="text-neutral-400 hover:text-rose-600"><X className="w-4 h-4" /></button>
            </div>
          ))}
          {!anomalies?.items?.length && <div className="text-sm text-neutral-500 text-center py-10">No anomalies right now.</div>}
        </div>
      )}

      {tab === "churn" && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-xs">
              <tr><th className="text-left px-4 py-2">Outlet</th><th>Last order</th><th>90d value</th><th>Risk</th></tr>
            </thead>
            <tbody>
              {(churn?.items || []).map(o => (
                <tr key={o.outlet_id} className="border-t" data-testid={`churn-${o.outlet_id}`}>
                  <td className="px-4 py-2">{o.outlet_code} · {o.outlet_name}</td>
                  <td className="text-center text-xs">{o.last_order_date || "never"}</td>
                  <td className="text-right">₹{Number(o.trend_90d_value || 0).toLocaleString("en-IN")}</td>
                  <td className="text-center">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${o.risk === "high" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                      {o.risk}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "forecast" && (
        <div className="bg-white border rounded-xl p-4" data-testid="forecast-panel">
          <div className="text-sm font-semibold mb-2">Sales forecast · confidence: <span className="text-[#E5197F]">{forecast?.data_confidence}</span></div>
          {forecast?.data_confidence === "insufficient_data" ? (
            <div className="text-sm text-neutral-500">{forecast?.note}</div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-neutral-500">History (last {forecast?.history?.length || 0} months) → Forecast (next {forecast?.forecast?.length || 0} months). MAPE: {forecast?.mape_pct}%</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-xs font-semibold text-neutral-500">History</div>
                  {(forecast?.history || []).map(h => (
                    <div key={h.month} className="flex justify-between border-b py-1"><span>{h.month}</span><span>₹{Number(h.value).toLocaleString("en-IN")}</span></div>
                  ))}
                </div>
                <div>
                  <div className="text-xs font-semibold text-neutral-500">Forecast (estimate)</div>
                  {(forecast?.forecast || []).map(f => (
                    <div key={f.month_offset} className="flex justify-between border-b py-1 text-[#E5197F]"><span>+{f.month_offset}m</span><span>₹{Number(f.value).toLocaleString("en-IN")}</span></div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "stockout" && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-xs">
              <tr><th className="text-left px-4 py-2">SKU</th><th>On-hand</th><th>Velocity/14d</th><th>Days supply</th><th>Score</th></tr>
            </thead>
            <tbody>
              {(stockOut?.items || []).map(r => (
                <tr key={r.sku_id + r.distributor_id} className="border-t" data-testid={`stockout-${r.sku_id}`}>
                  <td className="px-4 py-2">{r.sku_code} · {r.sku_name}</td>
                  <td className="text-right">{r.on_hand_qty}</td>
                  <td className="text-right">{r.velocity_14d}</td>
                  <td className="text-center">{r.days_of_supply ?? "—"}</td>
                  <td className="text-center">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${r.band === "red" ? "bg-rose-100 text-rose-700" : r.band === "amber" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {r.risk_score}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
